<template>
  <app-page-wrapper
    class="relative z-20 grid place-content-center !px-0"
    :class="{
      'supports-timeline-scroll:sticky-below-header supports-timeline-scroll:!top-[--layout-offset-top] supports-not-timeline-scroll:md:static':
        sticky,
    }"
    data-cy-catalog-navigation
  >
    <nav
      class="navigation relative max-w-full overflow-hidden rounded-b-lg px-2"
    >
      <div
        class="background absolute inset-0 origin-top rounded-b-lg border border-primary/10 bg-white/95 opacity-0 backdrop-blur"
      />
      <ul class="no-scrollbar relative flex gap-2 overflow-auto">
        <li v-for="item in items" :key="item.id">
          <catalog-navigation-item
            :label="item.label"
            :style="{
              backgroundColor: item.tag
                ? hexToHSLA(item.tag.color, { luminance: 97, saturation: 60 })
                : undefined,
              borderColor: item.tag
                ? hexToHSLA(item.tag.color, {
                    luminance: item.active ? 55 : 85,
                    saturation: item.active ? 40 : 50,
                  })
                : undefined,
            }"
            :to="item.to"
          >
            <template #prepend>
              <catalog-navigation-item-teachers v-if="item.id === '_top'" />
              <catalog-navigation-item-next-sessions
                v-if="item.id === '_search'"
              />
              <catalog-navigation-item-tag-icon
                v-else-if="item.tag"
                :active="item.active"
                :tag="item.tag"
              />
            </template>
          </catalog-navigation-item>
        </li>
      </ul>
    </nav>

    <div class="hidden">
      <app-icon
        v-for="item in tagPages"
        :key="item.id"
        :icon="iconFillVariant(item.tag.icon)"
      />
    </div>
  </app-page-wrapper>
</template>

<script lang="ts" setup>
import type { I18nMessage } from "@pollen/core/model/i18n.model";
import { iconFillVariant } from "@pollen/ui-library";

import type { RouteLocationRaw } from "#vue-router";
import { useTagPageList } from "~/business-areas/tag-page/composables/tag-page-list.hook";
import CatalogNavigationItem from "~/pages/catalog/_includes/components/old/catalog-navigation-item.vue";
import CatalogNavigationItemNextSessions from "~/pages/catalog/_includes/components/old/catalog-navigation-item-next-sessions-icon.vue";
import CatalogNavigationItemTagIcon from "~/pages/catalog/_includes/components/old/catalog-navigation-item-tag-icon.vue";
import CatalogNavigationItemTeachers from "~/pages/catalog/_includes/components/old/catalog-navigation-item-teacher-icon.vue";

withDefaults(
  defineProps<{
    sticky: boolean;
  }>(),
  {
    sticky: true,
  },
);

const { data: tagPages } = useTagPageList();

const route = useRoute();
const router = useRouter();

function isActive(to: RouteLocationRaw) {
  return router.resolve(to).path === route.path;
}

const items = computed<
  {
    id: string | number;
    label: I18nMessage;
    to: RouteLocationRaw;
    active: boolean;
    tag?: DatabaseTable<"tags">;
  }[]
>(() => {
  const list = [
    {
      id: "_top",
      label: "À la une",
      to: "/catalog/top-trainers",
    },
    {
      id: "_search",
      label: "Prochaines sessions",
      to: "/catalog/search",
    },
    ...tagPages.value.map((tagPage) => {
      return {
        id: tagPage.id,
        label: tagPage.tag.label,
        to: tagPage._links.page,
        tag: tagPage.tag,
      };
    }),
  ];
  return list.map((item) => {
    return {
      ...item,
      active: isActive(item.to),
    };
  });
});
</script>

<style scoped>
@supports (animation-timeline: scroll()) {
  @keyframes background {
    from {
      opacity: 0;
      transform: scale(0.6);
    }
    to {
      opacity: 100;
      transform: scale(1);
    }
  }

  @keyframes navigationAnimation {
    to {
      box-shadow: 0 0 16px rgba(139, 105, 196, 0.15);
      padding-top: theme("spacing.2");
      padding-bottom: theme("spacing.2");
    }
  }

  .navigation,
  .background {
    animation-fill-mode: both;
    animation-timing-function: ease-in-out;
    animation-timeline: scroll(root);
    animation-range: 0% 100px;
  }

  .navigation {
    animation-name: navigationAnimation;
  }

  .background {
    animation-name: background;
  }
}
</style>
